import type { PlayerId, WarchestState } from "@cloudnest/warchest-engine";
import { WC_FACTION_NAME, WC_UNIT_ABILITY, WC_UNIT_NAME } from "../i18n";

interface WarchestDraftPanelProps {
  readonly gameState: WarchestState;
  readonly myPlayerId: PlayerId | null;
  readonly onPick: (unitType: string) => void;
}

function PicksColumn({
  player,
  picks,
  unitsPerPlayer,
}: {
  player: PlayerId;
  picks: readonly string[];
  unitsPerPlayer: number;
}) {
  return (
    <div data-testid={`warchest-draft-picks-${player}`}>
      <div
        className="font-semibold text-xs"
        style={{
          color:
            player === "P1"
              ? "var(--cn-warchest-raven)"
              : "var(--cn-warchest-wolf)",
        }}
      >
        {WC_FACTION_NAME[player]} ({picks.length}/{unitsPerPlayer})
      </div>
      <div
        className="mt-1 text-sm"
        style={{ color: "var(--cn-warchest-text)" }}
      >
        {picks.length > 0
          ? picks.map((u) => WC_UNIT_NAME[u] ?? u).join(", ")
          : "—"}
      </div>
    </div>
  );
}

// Rendered while gameState.phase === "DRAFT". Both players see the shared pool
// and each other's picks (War Chest draft is open); only the active drafter
// can pick.
export function WarchestDraftPanel({
  gameState,
  myPlayerId,
  onPick,
}: WarchestDraftPanelProps) {
  const draft = gameState.draftState;
  if (!draft) {
    console.warn(
      "WarchestDraftPanel rendered without draftState (phase=%s)",
      gameState.phase,
    );
    return null;
  }

  const isMyTurn = gameState.activePlayer === myPlayerId;
  const activeName = WC_FACTION_NAME[gameState.activePlayer];

  return (
    <div
      data-testid="warchest-draft-panel"
      className="space-y-4 rounded-2xl p-4"
      style={{
        background: "var(--cn-warchest-card-bg)",
        border: "1px solid var(--cn-warchest-card-border)",
      }}
    >
      <div
        data-testid="warchest-draft-status"
        className="rounded-lg px-4 py-2 text-center font-semibold text-sm"
        style={{
          background: isMyTurn
            ? "var(--cn-warchest-selected)"
            : "var(--cn-warchest-valid-deploy)",
          color: "var(--cn-warchest-text)",
        }}
      >
        {isMyTurn ? "내 차례 — 유닛을 선택하세요" : `${activeName} 선택 중…`}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {draft.availableUnits.map((unit) => (
          <button
            key={unit}
            type="button"
            data-testid={`warchest-draft-pick-${unit}`}
            disabled={!isMyTurn}
            onClick={() => onPick(unit)}
            title={WC_UNIT_ABILITY[unit] ?? ""}
            className="rounded-lg p-2 text-center"
            style={{
              background: "var(--cn-warchest-bg)",
              border: "1px solid var(--cn-warchest-card-border)",
              color: "var(--cn-warchest-text)",
              opacity: isMyTurn ? 1 : 0.6,
              cursor: isMyTurn ? "pointer" : "default",
            }}
          >
            <div className="font-semibold text-xs leading-tight">
              {WC_UNIT_NAME[unit] ?? unit}
            </div>
            <div
              className="mt-0.5 text-xs leading-tight"
              style={{ color: "var(--cn-warchest-text-muted)" }}
            >
              {WC_UNIT_ABILITY[unit] ?? ""}
            </div>
          </button>
        ))}
      </div>

      <div
        className="grid grid-cols-2 gap-3 rounded-lg p-3"
        style={{ background: "var(--cn-warchest-bg)" }}
      >
        <PicksColumn
          player="P1"
          picks={draft.picks.P1 ?? []}
          unitsPerPlayer={draft.unitsPerPlayer}
        />
        <PicksColumn
          player="P2"
          picks={draft.picks.P2 ?? []}
          unitsPerPlayer={draft.unitsPerPlayer}
        />
      </div>
    </div>
  );
}
